import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Clock3,
  Droplet,
  HeartPulse,
  Minus,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Ruler,
  Smartphone,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildTc6Payload,
  computeTc6,
  computeTc6DerivedMetrics,
  computeTC6PrimaryReference,
  formatTc6StopReasonLabel,
  getTc6DistanceSeries,
  getTc6VitalsSeries,
  getTC6ReferenceLabel,
  isTc6AwaitingSecondAttempt,
  normalizeTC6ReferenceKey,
  TC6_REFERENCE_OPTIONS,
  type Tc6ChartImages,
  type Tc6PatientSnapshot,
  type Tc6ReferenceKey,
  type Tc6ResultPayload,
  type Tc6StopReason,
  type Tc6AttemptPayload,
} from '../tc6/tc6Domain';
import AssessmentWeightAdjustmentField from '../components/AssessmentWeightAdjustmentField';
import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../utils/patientMetrics';

type PatientContextInput = {
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
};

type AttemptFormState = {
  corridor: number;
  baseline: {
    hr: string;
    spo2: string;
    bpSys: string;
    bpDia: string;
    borgDyspnea: number | null;
    borgFatigue: number | null;
  };
  perMinute: Array<{
    minute: number;
    hr: string;
    spo2: string;
    lapTotal: string;
    extraMeters: string;
  }>;
  final: {
    hr: string;
    spo2: string;
    bpSys: string;
    bpDia: string;
    borgDyspnea: number | null;
    borgFatigue: number | null;
  };
  lapsCounter: number;
  liveLastLapMeters: number;
  running: boolean;
  elapsed: number;
  startedAt: string | null;
  endedAt: string | null;
  patientStopped: boolean;
  stopIntervals: Array<{
    start: number;
    end?: number;
    reason?: Tc6StopReason;
  }>;
  pendingStopReason: boolean;
};

interface TC6TestProps {
  initial?: (Tc6ResultPayload & { chartImages?: Tc6ChartImages }) | null;
  patientContext?: PatientContextInput;
  onCancel?: () => void;
  onDraftAttempt?: (payload: Tc6ResultPayload & { chartImages?: Tc6ChartImages }) => Promise<boolean | void> | boolean | void;
  onNext: (payload: Tc6ResultPayload & { chartImages?: Tc6ChartImages }) => void;
}

type MinutePromptFieldKey = 'hr' | 'spo2' | 'postTurn';
type MobileCaptureMode = 'assisted' | 'simplified';
type DesktopWorkflowStage = 'application' | 'closing';

type MinutePromptState = {
  open: boolean;
  minute: number;
  baseDistance: number;
  lapsSnapshot: number;
} & Record<MinutePromptFieldKey, string>;

const STOP_REASON_OPTIONS: Array<{ value: Tc6StopReason; label: string; description: string }> = [
  {
    value: 'patient_request',
    label: 'Paciente pediu para parar',
    description: 'Cansaço, dispneia ou limitação subjetiva.',
  },
  {
    value: 'supervisor_interruption',
    label: 'Interrompido pelo avaliador',
    description: 'Queda importante de SpO2 ou critério clínico.',
  },
];

const ENCOURAGEMENTS_PT: Record<number, string> = {
  1: 'Voce esta indo bem. Faltam 5 minutos.',
  2: 'Mantenha o bom trabalho. Faltam 4 minutos.',
  3: 'Voce esta indo bem. Metade do caminho.',
  4: 'Mantenha o bom trabalho. Restam apenas 2 minutos.',
  5: 'Voce esta indo bem. Falta apenas 1 minuto.',
  6: 'Por favor, pare onde estiver.',
};

const STOP_RESUME_MSG = 'Por favor, retome a caminhada assim que se sentir capaz.';
const TEN_SECONDS_MSG = '10 s para fechar o minuto. Observe a distância após a última volta.';

const toIsoNow = () => new Date().toISOString();
const pad = (value: number) => String(value).padStart(2, '0');
const formatClock = (seconds: number) => `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;

const buildEmptyAttemptForm = (corridor = 30): AttemptFormState => ({
  corridor,
  baseline: {
    hr: '',
    spo2: '',
    bpSys: '',
    bpDia: '',
    borgDyspnea: null,
    borgFatigue: null,
  },
  perMinute: Array.from({ length: 6 }, (_, index) => ({
    minute: index + 1,
    hr: '',
    spo2: '',
    lapTotal: '',
    extraMeters: '',
  })),
  final: {
    hr: '',
    spo2: '',
    bpSys: '',
    bpDia: '',
    borgDyspnea: null,
    borgFatigue: null,
  },
  lapsCounter: 0,
  liveLastLapMeters: 0,
  running: false,
  elapsed: 0,
  startedAt: null,
  endedAt: null,
  patientStopped: false,
  stopIntervals: [],
  pendingStopReason: false,
});

const parseOptionalNumber = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRequiredNumber = (value: string): number | null => {
  const parsed = parseOptionalNumber(value);
  return parsed != null ? parsed : null;
};

const buildPatientSnapshot = (patientContext?: PatientContextInput): Tc6PatientSnapshot | null => {
  if (!patientContext) return null;
  const age = typeof patientContext.ageYears === 'number' ? patientContext.ageYears : null;
  const height = typeof patientContext.heightCm === 'number' ? patientContext.heightCm : null;
  const weight = typeof patientContext.weightKg === 'number' ? patientContext.weightKg : null;
  const bmi = height && weight ? +(weight / Math.pow(height / 100, 2)).toFixed(1) : null;
  return {
    age,
    sex: patientContext.sex ?? null,
    height,
    weight,
    bmi,
  };
};

const buildTc6AttemptComputation = (form: AttemptFormState) => {
  const parsedLapTotals = form.perMinute.map((entry) => parseRequiredNumber(entry.lapTotal));
  const parsedExtras = form.perMinute.map((entry) => parseRequiredNumber(entry.extraMeters));
  const hasCompleteMinuteSeries = parsedLapTotals.every((value) => value != null) && parsedExtras.every((value) => value != null);

  const lastRecordedLapTotal = [...parsedLapTotals].reverse().find((value): value is number => value != null) ?? 0;
  const lastRecordedExtra = [...parsedExtras].reverse().find((value): value is number => value != null) ?? 0;
  const lastMinuteLapTotal = parsedLapTotals[parsedLapTotals.length - 1];
  const lastMinuteExtra = parsedExtras[parsedExtras.length - 1];
  const livePartialMeters = Number.isFinite(form.liveLastLapMeters) ? Math.max(0, form.liveLastLapMeters) : 0;
  const finalLapTotal = Math.max(0, lastMinuteLapTotal ?? form.lapsCounter ?? lastRecordedLapTotal);
  const finalExtraMeters = Math.max(0, lastMinuteExtra ?? (livePartialMeters > 0 ? livePartialMeters : lastRecordedExtra));

  if (!hasCompleteMinuteSeries) {
    const totalDistanceMeters = Math.round(finalLapTotal * form.corridor + finalExtraMeters);
    return {
      hasCompleteMinuteSeries,
      totalDistanceMeters,
      lapTotals: [] as number[],
      extras: [] as number[],
      minuteSegments: [] as number[],
      cumulative: [] as number[],
      speedByMinute: [] as number[],
      finalLapTotal,
      finalExtraMeters,
      avgSpeed_m_min: 0,
      avgSpeed_m_s: 0,
      avgSpeed_km_h: 0,
    };
  }

  const lapTotals = parsedLapTotals as number[];
  const extras = parsedExtras as number[];
  const minuteData = lapTotals.map((total, index) => {
    const previous = index > 0 ? lapTotals[index - 1] : 0;
    return {
      laps: Math.max(0, total - previous),
      extraMeters: extras[index] ?? 0,
    };
  });
  const metrics = computeTc6(minuteData, form.corridor);
  const cumulative = metrics.perMinute.reduce<number[]>((accumulator, value, index) => {
    const total = value + (index > 0 ? accumulator[index - 1] : 0);
    accumulator[index] = total;
    return accumulator;
  }, Array(metrics.perMinute.length).fill(0));
  const speedByMinute = metrics.perMinute.map((value) => +Math.max(0, value * 0.06).toFixed(2));

  return {
    hasCompleteMinuteSeries,
    totalDistanceMeters: Math.round(metrics.totalDistance),
    lapTotals,
    extras,
    minuteSegments: metrics.perMinute,
    cumulative,
    speedByMinute,
    finalLapTotal,
    finalExtraMeters,
    avgSpeed_m_min: metrics.avgSpeed_m_min,
    avgSpeed_m_s: metrics.avgSpeed_m_s,
    avgSpeed_km_h: metrics.avgSpeed_km_h,
  };
};

const buildAttemptSummaryCards = (attempt: Tc6AttemptPayload | null, selectedReferenceKey: Tc6ReferenceKey) => {
  if (!attempt) return [];
  const primaryReference = computeTC6PrimaryReference(attempt, selectedReferenceKey);
  return [
    {
      label: 'Distancia total',
      value: `${attempt.total_distance_m} m`,
      note: attempt.derivedMetrics?.meanSpeed_kmh != null ? `${attempt.derivedMetrics.meanSpeed_kmh} km/h em media` : null,
    },
    {
      label: 'Predito',
      value:
        primaryReference?.predicted != null
          ? `${primaryReference.predicted.toFixed(1)} m`
          : 'Não disponível',
      note:
        primaryReference?.percentPredicted != null
          ? `${primaryReference.percentPredicted.toFixed(1)}% do previsto`
          : getTC6ReferenceLabel(selectedReferenceKey),
    },
    {
      label: 'Paradas',
      value: attempt.stop_report?.intervals?.length ? `${attempt.stop_report.intervals.length}x` : 'Nenhuma',
      note: attempt.stop_report?.total_stopped_fmt ?? null,
    },
  ];
};

const BORG_VALUES: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const BORG_LABELS: Record<number, string> = {
  0: 'Nenhuma',
  0.5: 'Muito, muito leve',
  1: 'Muito leve',
  2: 'Leve',
  3: 'Moderada',
  4: 'Pouco intensa',
  5: 'Intensa',
  6: '',
  7: 'Muito intensa',
  8: '',
  9: 'Muito, muito intensa',
  10: 'Maxima',
};

const formatBorgValue = (value: number | null) => {
  if (value == null) return '';
  return String(value).replace('.', ',');
};

const hasFilledMeasure = (value: string) => value.trim().length > 0;

const getMissingBaselineFields = (baseline: AttemptFormState['baseline']) => {
  const missing: string[] = [];
  if (!hasFilledMeasure(baseline.spo2)) missing.push('SpO2');
  if (!hasFilledMeasure(baseline.hr)) missing.push('FC');
  if (!hasFilledMeasure(baseline.bpSys)) missing.push('PA sistolica');
  if (!hasFilledMeasure(baseline.bpDia)) missing.push('PA diastolica');
  if (baseline.borgDyspnea == null) missing.push('Borg dispneia');
  if (baseline.borgFatigue == null) missing.push('Borg fadiga');
  return missing;
};

const getMissingFinalFields = (finalMeasures: AttemptFormState['final']) => {
  const missing: string[] = [];
  if (!hasFilledMeasure(finalMeasures.spo2)) missing.push('SpO2');
  if (!hasFilledMeasure(finalMeasures.hr)) missing.push('FC');
  if (!hasFilledMeasure(finalMeasures.bpSys)) missing.push('PA sistolica');
  if (!hasFilledMeasure(finalMeasures.bpDia)) missing.push('PA diastolica');
  if (finalMeasures.borgDyspnea == null) missing.push('Borg dispneia');
  if (finalMeasures.borgFatigue == null) missing.push('Borg fadiga');
  return missing;
};

const BorderTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div>
    <h4 className="text-base font-semibold text-slate-900">{title}</h4>
    {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
  </div>
);

const Field = ({
  label,
  value,
  onChange,
  type = 'text',
  min,
  max,
  step,
  suffix,
  disabled = false,
  error = false,
}: {
  label: string;
  value: string | number;
  onChange: (next: string) => void;
  type?: string;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  disabled?: boolean;
  error?: boolean;
}) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-50 ${
          error
            ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
            : 'border-slate-200 focus:border-primary-500 focus:ring-primary-500'
        }`}
      />
      {suffix ? <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span> : null}
    </div>
  </label>
);

const BorgField = ({
  label,
  value,
  onChange,
  error = false,
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  error?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex w-full items-center justify-between rounded-xl border bg-white px-3 py-2 text-left text-sm text-slate-900 ${
          error ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200'
        }`}
      >
        <div>
          <div className="font-semibold">{value == null ? 'Abrir escala de Borg' : `Borg ${formatBorgValue(value)}`}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {value == null ? 'Mostre a escala ao paciente e registre a resposta.' : BORG_LABELS[value] || 'Valor registrado'}
          </div>
        </div>
        <span className="text-xs font-semibold text-primary-600">Ver escala</span>
      </button>
      {value != null ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-2 text-xs text-slate-600 underline hover:text-slate-800"
        >
          Limpar valor
        </button>
      ) : null}

      {open ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-4xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-primary-600">Escala de Borg CR10</div>
                <h4 className="mt-2 text-xl font-semibold text-slate-900">{label}</h4>
                <p className="mt-1 text-sm text-slate-600">
                  Mostre a escala ao paciente, confirme a percepção e selecione abaixo o valor informado.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Fechar
              </button>
            </div>

            <div className="mt-5 overflow-x-auto">
              <div className="inline-grid min-w-full grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12">
                {BORG_VALUES.map((borgValue) => (
                  <button
                    key={borgValue}
                    type="button"
                    onClick={() => {
                      onChange(borgValue);
                      setOpen(false);
                    }}
                    className={`flex min-w-[96px] flex-col items-center justify-center gap-1 rounded-2xl border p-3 text-center transition-colors ${
                      value === borgValue
                        ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-base font-semibold leading-tight">{formatBorgValue(borgValue)}</div>
                    {BORG_LABELS[borgValue] ? (
                      <div className="text-[11px] leading-tight opacity-80 whitespace-pre-line">{BORG_LABELS[borgValue]}</div>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              {value != null ? (
                <button
                  type="button"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Limpar valor
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const AttemptMetricCard = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">{label}</div>
    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

export default function TC6Test({ initial = null, patientContext, onCancel, onDraftAttempt, onNext }: TC6TestProps) {
  const initialReference = normalizeTC6ReferenceKey(initial?.referenceEquation);
  const initialChartImages = initial?.chartImages ?? {};
  const initialAttempt1 = initial?.attempt1 ?? null;
  const initialAwaitingSecondAttempt = isTc6AwaitingSecondAttempt(initial);
  const initialWeightKg = normalizePatientWeightKg(initial?.patientContext?.weightKg ?? patientContext?.weightKg);

  const [selectedReferenceKey, setSelectedReferenceKey] = useState<Tc6ReferenceKey>(initialReference);
  const [attemptStage, setAttemptStage] = useState<'attempt1' | 'decision' | 'attempt2'>(
    initialAwaitingSecondAttempt && initialAttempt1 ? 'decision' : 'attempt1',
  );
  const [attemptNumber, setAttemptNumber] = useState<1 | 2>(initialAwaitingSecondAttempt && initialAttempt1 ? 2 : 1);
  const [attempt1Result, setAttempt1Result] = useState<Tc6AttemptPayload | null>(initialAttempt1);
  const [savedChartImages, setSavedChartImages] = useState<Tc6ChartImages>(initialChartImages);
  const [form, setForm] = useState<AttemptFormState>(
    buildEmptyAttemptForm(initialAttempt1?.corridorLength_m ?? 30),
  );
  const [perMinuteCompleted, setPerMinuteCompleted] = useState<boolean[]>(Array(6).fill(false));
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [savingAttempt, setSavingAttempt] = useState(false);
  const [weightKg, setWeightKg] = useState<number | null>(initialWeightKg);
  const [applicationMode, setApplicationMode] = useState(false);
  const [mobileCaptureMode, setMobileCaptureMode] = useState<MobileCaptureMode>('assisted');
  const [desktopWorkflowStage, setDesktopWorkflowStage] = useState<DesktopWorkflowStage>('application');
  const [showMobileBaselineModal, setShowMobileBaselineModal] = useState(false);
  const [showMobileFinalModal, setShowMobileFinalModal] = useState(false);
  const [baselineModalError, setBaselineModalError] = useState<string | null>(null);
  const [finalModalError, setFinalModalError] = useState<string | null>(null);
  const [showEnc, setShowEnc] = useState(false);
  const [encText, setEncText] = useState('');
  const [minutePrompt, setMinutePrompt] = useState<MinutePromptState>({
    open: false,
    minute: 1,
    hr: '',
    spo2: '',
    postTurn: '',
    baseDistance: 0,
    lapsSnapshot: 0,
  });
  const [minutePromptError, setMinutePromptError] = useState<string | null>(null);
  const [minutePromptCountdown, setMinutePromptCountdown] = useState<number>(60);
  const [activeMinuteField, setActiveMinuteField] = useState<MinutePromptFieldKey>('spo2');
  const [scheduledMinutePrompt, setScheduledMinutePrompt] = useState<number | null>(null);

  const timerRef = useRef<number | null>(null);
  const stopOpenedAtRef = useRef<number | null>(null);
  const stopReminderRef = useRef<number | null>(null);
  const encTimeoutRef = useRef<number | null>(null);
  const minutePromptTimerRef = useRef<number | null>(null);
  const minutePromptDelayRef = useRef<number | null>(null);
  const captureDistanceChartRef = useRef<HTMLDivElement | null>(null);
  const captureVitalsChartRef = useRef<HTMLDivElement | null>(null);

  const effectivePatientContext = useMemo(
    () => ({
      ...patientContext,
      ageYears: toFiniteNumber(patientContext?.ageYears),
      weightKg,
      heightCm: normalizePatientHeightCm(patientContext?.heightCm),
    }),
    [patientContext, weightKg],
  );
  const patientSnapshot = useMemo(() => buildPatientSnapshot(effectivePatientContext), [effectivePatientContext]);
  const baselineMissingFields = useMemo(() => getMissingBaselineFields(form.baseline), [form.baseline]);
  const finalMissingFields = useMemo(() => getMissingFinalFields(form.final), [form.final]);
  const baselineComplete = baselineMissingFields.length === 0;
  const finalComplete = finalMissingFields.length === 0;
  const attemptFinished = form.elapsed >= 360;
  const minuteCompletedCount = perMinuteCompleted.filter(Boolean).length;
  const pendingMinutesCount = perMinuteCompleted.length - minuteCompletedCount;
  const passiveMinute = form.elapsed >= 360 ? 6 : Math.min(6, Math.floor(form.elapsed / 60) + 1);
  const applicationBaselineStage = applicationMode && !form.startedAt && form.elapsed === 0;
  const applicationRunningStage = applicationMode && !applicationBaselineStage;
  const mobileAssistedMode = applicationMode && mobileCaptureMode === 'assisted';
  const mobileSimplifiedMode = applicationMode && mobileCaptureMode === 'simplified';
  const desktopClosingStage = !applicationMode && desktopWorkflowStage === 'closing';
  const remainingSeconds = Math.max(0, 360 - form.elapsed);

  const corridorLocked = attemptNumber === 2 && !!attempt1Result;

  const currentAttemptPreview = useMemo(() => {
    const computation = buildTc6AttemptComputation(form);
    const provisionalElapsed = form.elapsed > 0 ? Math.min(form.elapsed, 360) : 360;
    const provisionalAttempt: Tc6AttemptPayload = {
      corridorLength_m: form.corridor,
      laps: computation.finalLapTotal,
      last_lap_meters: computation.finalExtraMeters,
      total_distance_m: computation.totalDistanceMeters,
      derivedMetrics: computeTc6DerivedMetrics(computation.totalDistanceMeters, provisionalElapsed, patientSnapshot),
      baseline: {
        hr: form.baseline.hr || null,
        spo2: form.baseline.spo2 || null,
        bp: { sys: form.baseline.bpSys || null, dia: form.baseline.bpDia || null },
        borg: { dyspnea: form.baseline.borgDyspnea, fatigue: form.baseline.borgFatigue },
      },
      per_minute: form.perMinute.map((entry) => ({
        minute: entry.minute,
        hr: entry.hr || null,
        spo2: entry.spo2 || null,
      })),
      per_minute_distance_m: computation.cumulative,
      per_minute_speed_kmh: computation.speedByMinute,
      per_minute_segments_m: computation.minuteSegments,
      lap_totals_by_minute: computation.hasCompleteMinuteSeries ? computation.lapTotals : undefined,
      extra_by_minute: computation.hasCompleteMinuteSeries ? computation.extras : undefined,
      final: {
        hr: form.final.hr || null,
        spo2: form.final.spo2 || null,
        bp: { sys: form.final.bpSys || null, dia: form.final.bpDia || null },
        borg: { dyspnea: form.final.borgDyspnea, fatigue: form.final.borgFatigue },
      },
      stop_report: {
        total_stopped_s: 0,
        total_stopped_fmt: '00:00',
        intervals: [],
      },
      timer: {
        elapsed_s: provisionalElapsed,
        completed: provisionalElapsed >= 360,
        startedAt: form.startedAt,
        endedAt: form.endedAt,
      },
      patientSnapshot,
      tc6Speeds: {
        avg_m_min: computation.avgSpeed_m_min,
        avg_m_s: computation.avgSpeed_m_s,
        avg_km_h: computation.avgSpeed_km_h,
      },
      finishedAt: toIsoNow(),
    };
    const primaryReference = computeTC6PrimaryReference(provisionalAttempt, selectedReferenceKey);
    return {
      attempt: provisionalAttempt,
      distanceSeries: getTc6DistanceSeries(provisionalAttempt, selectedReferenceKey),
      vitalsSeries: getTc6VitalsSeries(provisionalAttempt),
      predictedDistance: primaryReference?.predicted ?? null,
    };
  }, [form, patientSnapshot, selectedReferenceKey]);

  const minuteFieldConfigs: Array<{ field: MinutePromptFieldKey; label: string; unit: string; icon: JSX.Element }> = [
    { field: 'spo2', label: 'SpO2 (%)', unit: '%', icon: <Droplet className="h-8 w-8 text-blue-500" /> },
    { field: 'hr', label: 'FC (bpm)', unit: 'bpm', icon: <HeartPulse className="h-8 w-8 text-rose-500" /> },
    { field: 'postTurn', label: 'Metros após a última volta', unit: 'm', icon: <Ruler className="h-8 w-8 text-emerald-500" /> },
  ];

  const keypadRows: string[][] = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['0', '00', '.'],
  ];

  const clearScheduledMinutePrompt = useCallback(() => {
    if (minutePromptDelayRef.current) {
      window.clearTimeout(minutePromptDelayRef.current);
      minutePromptDelayRef.current = null;
    }
    setScheduledMinutePrompt(null);
  }, []);

  const showAnnouncement = useCallback((message: string) => {
    if (!message) return;
    setEncText(message);
    setShowEnc(true);
    if (encTimeoutRef.current) {
      window.clearTimeout(encTimeoutRef.current);
    }
    encTimeoutRef.current = window.setTimeout(() => setShowEnc(false), 5000);
  }, []);

  const openMinutePromptForMinute = useCallback(
    (minute: number) => {
      const index = minute - 1;
      if (index < 0 || index > 5) return;

      const previousLapTotal = index > 0 ? parseRequiredNumber(form.perMinute[index - 1]?.lapTotal ?? '') ?? 0 : 0;
      const previousExtra = index > 0 ? parseRequiredNumber(form.perMinute[index - 1]?.extraMeters ?? '') ?? 0 : 0;
      const existingExtra = parseRequiredNumber(form.perMinute[index]?.extraMeters ?? '');
      const existingLapTotal = parseRequiredNumber(form.perMinute[index]?.lapTotal ?? '');

      setMinutePrompt({
        open: true,
        minute,
        hr: form.perMinute[index]?.hr ?? '',
        spo2: form.perMinute[index]?.spo2 ?? '',
        postTurn: existingExtra != null ? String(existingExtra) : '',
        baseDistance: previousLapTotal * form.corridor + previousExtra,
        lapsSnapshot: existingLapTotal ?? form.lapsCounter,
      });
      setActiveMinuteField('spo2');
      setMinutePromptError(null);
      setMinutePromptCountdown(60);
    },
    [form],
  );

  useEffect(() => {
    if (!form.running) {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = window.setInterval(() => {
      setForm((current) => {
        const nextElapsed = Math.min(current.elapsed + 1, 360);
        const shouldStop = nextElapsed >= 360;
        return {
          ...current,
          elapsed: nextElapsed,
          running: !shouldStop,
          endedAt: shouldStop ? current.endedAt ?? toIsoNow() : current.endedAt,
        };
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [form.running]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!applicationMode) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [applicationMode]);

  useEffect(() => {
    if (!applicationMode && attemptFinished) {
      setDesktopWorkflowStage('closing');
    }
  }, [applicationMode, attemptFinished]);

  useEffect(() => {
    const currentMinute = Math.floor(form.elapsed / 60);
    const index = currentMinute - 1;
    const shouldTriggerMinutePrompt =
      form.elapsed > 0 &&
      form.elapsed % 60 === 0 &&
      currentMinute <= 6 &&
      index >= 0 &&
      !perMinuteCompleted[index];

    if (shouldTriggerMinutePrompt && !minutePrompt.open && scheduledMinutePrompt !== currentMinute) {
      if (mobileAssistedMode) {
        if (minutePromptDelayRef.current) {
          window.clearTimeout(minutePromptDelayRef.current);
        }
        setScheduledMinutePrompt(currentMinute);
        minutePromptDelayRef.current = window.setTimeout(() => {
          openMinutePromptForMinute(currentMinute);
          minutePromptDelayRef.current = null;
          setScheduledMinutePrompt(null);
        }, 5000);
      } else if (!applicationMode) {
        openMinutePromptForMinute(currentMinute);
      }
    }

    if (form.patientStopped && stopOpenedAtRef.current != null) {
      const stoppedFor = form.elapsed - stopOpenedAtRef.current;
      if (stoppedFor >= 30) {
        const lastReminder = stopReminderRef.current;
        if (lastReminder == null || form.elapsed - lastReminder >= 30) {
          showAnnouncement(STOP_RESUME_MSG);
          stopReminderRef.current = form.elapsed;
        }
      }
    }

    if (form.elapsed > 0 && form.elapsed < 360 && form.elapsed % 60 === 50) {
      showAnnouncement(TEN_SECONDS_MSG);
    }

    if (shouldTriggerMinutePrompt) {
      const message = ENCOURAGEMENTS_PT[currentMinute] ?? '';
      if (message) {
        showAnnouncement(message);
      }
    }
  }, [
    applicationMode,
    form.elapsed,
    form.patientStopped,
    mobileAssistedMode,
    minutePrompt.open,
    openMinutePromptForMinute,
    perMinuteCompleted,
    scheduledMinutePrompt,
    showAnnouncement,
  ]);

  useEffect(() => {
    if (minutePrompt.open) {
      if (minutePromptTimerRef.current) {
        window.clearInterval(minutePromptTimerRef.current);
      }
      setMinutePromptCountdown(60);
      minutePromptTimerRef.current = window.setInterval(() => {
        setMinutePromptCountdown((current) => {
          if (current <= 1) {
            if (minutePromptTimerRef.current) {
              window.clearInterval(minutePromptTimerRef.current);
              minutePromptTimerRef.current = null;
            }
            clearScheduledMinutePrompt();
            setMinutePrompt({
              open: false,
              minute: 1,
              hr: '',
              spo2: '',
              postTurn: '',
              baseDistance: 0,
              lapsSnapshot: 0,
            });
            setMinutePromptError(null);
            setActiveMinuteField('spo2');
            return 60;
          }
          return current - 1;
        });
      }, 1000);
    } else if (minutePromptTimerRef.current) {
      window.clearInterval(minutePromptTimerRef.current);
      minutePromptTimerRef.current = null;
      setMinutePromptCountdown(60);
    }

    return () => {
      if (minutePromptTimerRef.current) {
        window.clearInterval(minutePromptTimerRef.current);
        minutePromptTimerRef.current = null;
      }
    };
  }, [clearScheduledMinutePrompt, minutePrompt.open]);

  useEffect(() => {
    return () => {
      if (encTimeoutRef.current) {
        window.clearTimeout(encTimeoutRef.current);
      }
      if (minutePromptDelayRef.current) {
        window.clearTimeout(minutePromptDelayRef.current);
      }
      if (minutePromptTimerRef.current) {
        window.clearInterval(minutePromptTimerRef.current);
      }
    };
  }, []);

  const updatePerMinuteField = useCallback(
    (index: number, key: 'hr' | 'spo2' | 'lapTotal' | 'extraMeters', value: string) => {
      setForm((current) => {
        const nextPerMinute = current.perMinute.map((entry, entryIndex) =>
          entryIndex === index ? { ...entry, [key]: value } : entry,
        );
        const nextEntry = nextPerMinute[index];
        setPerMinuteCompleted((completed) =>
          completed.map((isComplete, entryIndex) =>
            entryIndex === index
              ? hasFilledMeasure(nextEntry.hr) &&
                hasFilledMeasure(nextEntry.spo2) &&
                hasFilledMeasure(nextEntry.lapTotal) &&
                hasFilledMeasure(nextEntry.extraMeters)
              : isComplete,
          ),
        );
        return {
          ...current,
          perMinute: nextPerMinute,
        };
      });
    },
    [],
  );

  const updateBaselineField = useCallback(
    (key: keyof AttemptFormState['baseline'], value: string | number | null) => {
      setInlineError(null);
      setBaselineModalError(null);
      setForm((current) => ({
        ...current,
        baseline: {
          ...current.baseline,
          [key]: value,
        },
      }));
    },
    [],
  );

  const updateFinalField = useCallback(
    (key: keyof AttemptFormState['final'], value: string | number | null) => {
      setInlineError(null);
      setFinalModalError(null);
      setForm((current) => ({
        ...current,
        final: {
          ...current.final,
          [key]: value,
        },
      }));
    },
    [],
  );

  const handleKeypadInput = (token: string) => {
    setMinutePrompt((current) => {
      const previous = current[activeMinuteField] || '';
      if (token === 'clear') {
        return { ...current, [activeMinuteField]: '' };
      }
      if (token === 'back') {
        return { ...current, [activeMinuteField]: previous.slice(0, -1) };
      }
      return { ...current, [activeMinuteField]: previous + token };
    });
    setMinutePromptError(null);
  };

  const resetMinutePrompt = useCallback(() => {
    clearScheduledMinutePrompt();
    setMinutePrompt({
      open: false,
      minute: 1,
      hr: '',
      spo2: '',
      postTurn: '',
      baseDistance: 0,
      lapsSnapshot: 0,
    });
    setMinutePromptError(null);
    setMinutePromptCountdown(60);
    setActiveMinuteField('spo2');
  }, [clearScheduledMinutePrompt]);

  const handleMinutePromptSave = () => {
    const index = minutePrompt.minute - 1;
    const rawMetersAfterTurn = String(minutePrompt.postTurn ?? '').trim().replace(',', '.');
    const metersAfterTurn = rawMetersAfterTurn.length ? Number(rawMetersAfterTurn) : 0;

    if (!hasFilledMeasure(minutePrompt.hr) || !hasFilledMeasure(minutePrompt.spo2)) {
      setMinutePromptError('Informe SpO2 e FC do minuto antes de salvar.');
      return;
    }
    if (Number.isNaN(metersAfterTurn) || metersAfterTurn < 0) {
      setMinutePromptError('Informe os metros percorridos após a última volta.');
      return;
    }
    if (metersAfterTurn >= form.corridor) {
      setMinutePromptError(`O valor deve ser menor que o comprimento do corredor (${form.corridor} m).`);
      return;
    }

    setForm((current) => ({
      ...current,
      liveLastLapMeters: metersAfterTurn,
      perMinute: current.perMinute.map((entry, entryIndex) =>
        entryIndex === index
          ? {
              ...entry,
              hr: minutePrompt.hr,
              spo2: minutePrompt.spo2,
              lapTotal: String(minutePrompt.lapsSnapshot),
              extraMeters: String(metersAfterTurn),
            }
          : entry,
      ),
    }));
    setPerMinuteCompleted((current) => current.map((value, entryIndex) => (entryIndex === index ? true : value)));
    resetMinutePrompt();
  };

  const handleMinutePromptSkip = () => {
    resetMinutePrompt();
  };

  const getStartValidationMessage = () => {
    if (!baselineComplete) {
      return `Preencha ${baselineMissingFields.join(', ')} antes de iniciar o TC6.`;
    }

    if (!(form.corridor > 0)) {
      return 'Informe um comprimento de corredor valido antes de iniciar.';
    }

    return null;
  };

  const startCurrentAttempt = () => {
    setInlineError(null);
    setBaselineModalError(null);
    setShowMobileBaselineModal(false);
    setDesktopWorkflowStage('application');
    setForm((current) => ({
      ...current,
      running: true,
      startedAt: current.startedAt ?? toIsoNow(),
      endedAt: null,
    }));
  };

  const handleStart = () => {
    const validationMessage = getStartValidationMessage();
    if (validationMessage) {
      if (applicationMode) {
        setBaselineModalError(validationMessage);
        setShowMobileBaselineModal(true);
      } else {
        setInlineError(validationMessage);
      }
      return;
    }

    startCurrentAttempt();
  };

  const handlePause = () => {
    setForm((current) => ({
      ...current,
      running: false,
      endedAt: current.elapsed >= 360 ? current.endedAt ?? toIsoNow() : current.endedAt,
    }));
  };

  const resetCurrentAttempt = useCallback(() => {
    setForm(buildEmptyAttemptForm(attempt1Result?.corridorLength_m ?? 30));
    setPerMinuteCompleted(Array(6).fill(false));
    setDesktopWorkflowStage('application');
    setInlineError(null);
    setBaselineModalError(null);
    setFinalModalError(null);
    setShowMobileBaselineModal(false);
    setShowMobileFinalModal(false);
    setShowEnc(false);
    if (encTimeoutRef.current) {
      window.clearTimeout(encTimeoutRef.current);
      encTimeoutRef.current = null;
    }
    resetMinutePrompt();
    stopReminderRef.current = null;
    stopOpenedAtRef.current = null;
  }, [attempt1Result, resetMinutePrompt]);

  const incrementLapCounter = (delta: number) => {
    setForm((current) => ({
      ...current,
      lapsCounter: Math.max(0, current.lapsCounter + delta),
    }));
  };

  const syncCurrentCounterToMinute = (minuteIndex: number) => {
    setForm((current) => {
      const nextPerMinute = current.perMinute.map((entry, index) =>
        index === minuteIndex
          ? {
              ...entry,
              lapTotal: String(current.lapsCounter),
              extraMeters: entry.extraMeters || String(current.liveLastLapMeters),
            }
          : entry,
      );
      const nextEntry = nextPerMinute[minuteIndex];
      setPerMinuteCompleted((completed) =>
        completed.map((value, index) =>
          index === minuteIndex
            ? hasFilledMeasure(nextEntry.hr) &&
              hasFilledMeasure(nextEntry.spo2) &&
              hasFilledMeasure(nextEntry.lapTotal) &&
              hasFilledMeasure(nextEntry.extraMeters)
            : value,
        ),
      );
      return {
        ...current,
        perMinute: nextPerMinute,
      };
    });
  };

  const beginStop = (reason: Tc6StopReason) => {
    const stopStartedAt = form.elapsed;
    setForm((current) => ({
      ...current,
      patientStopped: true,
      pendingStopReason: false,
      stopIntervals: [...current.stopIntervals, { start: stopStartedAt, reason }],
    }));
    stopOpenedAtRef.current = stopStartedAt;
    stopReminderRef.current = null;
  };

  const resumeFromStop = () => {
    setForm((current) => ({
      ...current,
      patientStopped: false,
      stopIntervals: current.stopIntervals.map((interval, index, array) =>
        index === array.length - 1 && interval.end == null ? { ...interval, end: current.elapsed } : interval,
      ),
    }));
    stopOpenedAtRef.current = null;
    stopReminderRef.current = null;
  };

  const validateCurrentAttempt = (): string | null => {
    const baseline = form.baseline;
    if (!baseline.hr || !baseline.spo2 || !baseline.bpSys || !baseline.bpDia || baseline.borgDyspnea == null || baseline.borgFatigue == null) {
      return 'Preencha todas as medidas basais do TC6.';
    }

    const final = form.final;
    if (!final.hr || !final.spo2 || !final.bpSys || !final.bpDia || final.borgDyspnea == null || final.borgFatigue == null) {
      return 'Preencha todas as medidas finais do TC6.';
    }

    if (!(form.corridor > 0)) {
        return 'Informe um comprimento de corredor válido.';
    }

    let previousLaps = 0;
    for (const minuteRow of form.perMinute) {
      const lapTotal = parseRequiredNumber(minuteRow.lapTotal);
      const extraMeters = parseRequiredNumber(minuteRow.extraMeters);
      const hasAnyMinuteField =
        hasFilledMeasure(minuteRow.hr) ||
        hasFilledMeasure(minuteRow.spo2) ||
        lapTotal != null ||
        extraMeters != null;

      if (!hasAnyMinuteField) {
        continue;
      }

      if (!minuteRow.hr || !minuteRow.spo2 || lapTotal == null || extraMeters == null) {
        return `Complete SpO2, FC, voltas acumuladas e metros após a última volta do minuto ${minuteRow.minute}, ou deixe o minuto inteiro em branco.`;
      }
      if (extraMeters < 0 || extraMeters >= form.corridor) {
        return `O valor de metros após a última volta do minuto ${minuteRow.minute} deve ser maior ou igual a 0 e menor que ${form.corridor} m.`;
      }
      if (lapTotal < previousLaps) {
        return `As voltas acumuladas do minuto ${minuteRow.minute} não podem ser menores que o minuto anterior.`;
      }
      previousLaps = lapTotal;
    }

    return null;
  };

  const exportCurrentCharts = useCallback(async (): Promise<{ distance_payload?: string; vitals_payload?: string }> => {
    const out: { distance_payload?: string; vitals_payload?: string } = {};
    const renderElementToCanvas = async (element: HTMLElement) => {
      const html2canvas = (await import('html2canvas')).default;
      return html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
    };

    const distanceNode = captureDistanceChartRef.current;
    const vitalsNode = captureVitalsChartRef.current;
    if (!distanceNode && !vitalsNode) return out;

    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    if (distanceNode) {
      const canvas = await renderElementToCanvas(distanceNode);
      out.distance_payload = canvas.toDataURL('image/png');
    }

    if (vitalsNode) {
      const canvas = await renderElementToCanvas(vitalsNode);
      out.vitals_payload = canvas.toDataURL('image/png');
    }

    return out;
  }, []);

  const buildAttemptPayload = async (): Promise<Tc6AttemptPayload | null> => {
    const validationMessage = validateCurrentAttempt();
    if (validationMessage) {
      setInlineError(validationMessage);
      return null;
    }

    const computation = buildTc6AttemptComputation(form);

    let stopIntervals = form.stopIntervals;
    if (form.patientStopped && stopIntervals.length) {
      stopIntervals = stopIntervals.map((interval, index, array) =>
        index === array.length - 1 && interval.end == null
          ? { ...interval, end: form.elapsed > 0 ? form.elapsed : 360 }
          : interval,
      );
    }

    const closedIntervals = stopIntervals.filter((interval): interval is Required<typeof interval> => typeof interval.end === 'number');
    const totalStoppedSeconds = closedIntervals.reduce((sum, interval) => sum + Math.max(0, interval.end - interval.start), 0);
    const resolvedElapsed = form.elapsed > 0 ? Math.min(form.elapsed, 360) : 360;
    const totalDistanceMeters = computation.totalDistanceMeters;
    const derivedMetrics = computeTc6DerivedMetrics(totalDistanceMeters, resolvedElapsed, patientSnapshot);

    return {
      corridorLength_m: form.corridor,
      laps: computation.finalLapTotal,
      last_lap_meters: computation.finalExtraMeters,
      total_distance_m: totalDistanceMeters,
      derivedMetrics,
      baseline: {
        hr: form.baseline.hr || null,
        spo2: form.baseline.spo2 || null,
        bp: { sys: form.baseline.bpSys || null, dia: form.baseline.bpDia || null },
        borg: { dyspnea: form.baseline.borgDyspnea, fatigue: form.baseline.borgFatigue },
      },
      per_minute: form.perMinute.map((entry) => ({
        minute: entry.minute,
        hr: entry.hr || null,
        spo2: entry.spo2 || null,
      })),
      per_minute_distance_m: computation.cumulative,
      per_minute_speed_kmh: computation.speedByMinute,
      per_minute_segments_m: computation.minuteSegments,
      lap_totals_by_minute: computation.hasCompleteMinuteSeries ? computation.lapTotals : undefined,
      extra_by_minute: computation.hasCompleteMinuteSeries ? computation.extras : undefined,
      final: {
        hr: form.final.hr || null,
        spo2: form.final.spo2 || null,
        bp: { sys: form.final.bpSys || null, dia: form.final.bpDia || null },
        borg: { dyspnea: form.final.borgDyspnea, fatigue: form.final.borgFatigue },
      },
      stop_report: {
        total_stopped_s: totalStoppedSeconds,
        total_stopped_fmt: formatClock(totalStoppedSeconds),
        intervals: closedIntervals.map((interval) => ({
          from_s: interval.start,
          to_s: interval.end,
          duration_s: Math.max(0, interval.end - interval.start),
          from_fmt: formatClock(interval.start),
          to_fmt: formatClock(interval.end),
          duration_fmt: formatClock(Math.max(0, interval.end - interval.start)),
          reason: interval.reason ?? null,
          reason_label: interval.reason ? formatTc6StopReasonLabel(interval.reason) : null,
        })),
      },
      timer: {
        elapsed_s: resolvedElapsed,
        completed: resolvedElapsed >= 360,
        startedAt: form.startedAt ?? toIsoNow(),
        endedAt: form.endedAt ?? (resolvedElapsed >= 360 ? toIsoNow() : null),
      },
      patientSnapshot,
      tc6Speeds: {
        avg_m_min: computation.avgSpeed_m_min,
        avg_m_s: computation.avgSpeed_m_s,
        avg_km_h: computation.avgSpeed_km_h,
      },
      finishedAt: toIsoNow(),
    };
  };

  const prefixChartImages = (
    attemptKey: 'attempt1' | 'attempt2',
    chartImages: { distance_payload?: string; vitals_payload?: string },
  ): Tc6ChartImages => ({
    ...(chartImages.distance_payload ? { [`${attemptKey}_distance_payload`]: chartImages.distance_payload } : {}),
    ...(chartImages.vitals_payload ? { [`${attemptKey}_vitals_payload`]: chartImages.vitals_payload } : {}),
  });

  const handleSaveAttempt = async (options?: { bypassMobileFinalGuard?: boolean }) => {
    if (savingAttempt) return;

    if (applicationMode && !options?.bypassMobileFinalGuard && !finalComplete) {
      setFinalModalError(`Preencha ${finalMissingFields.join(', ')} antes de concluir esta tentativa.`);
      setShowMobileFinalModal(true);
      return;
    }

    setSavingAttempt(true);
    setInlineError(null);
    try {
      const attemptPayload = await buildAttemptPayload();
      if (!attemptPayload) {
        return;
      }

      const currentImages = prefixChartImages(attemptNumber === 1 ? 'attempt1' : 'attempt2', await exportCurrentCharts());

      if (attemptNumber === 1) {
        const payload = buildTc6Payload(attemptPayload, {
          workflowStatus: 'awaiting_second_attempt',
          selectedReferenceKey,
          chartImages: currentImages,
          patientContext: effectivePatientContext,
        });

        if (onDraftAttempt) {
          const persisted = await onDraftAttempt(payload);
          if (persisted === false) return;
        }

        setAttempt1Result(attemptPayload);
        setSavedChartImages(currentImages);
        setAttemptStage('decision');
        setAttemptNumber(2);
        setDesktopWorkflowStage('application');
        setShowMobileFinalModal(false);
        setFinalModalError(null);
        setPerMinuteCompleted(Array(6).fill(false));
        resetMinutePrompt();
        setForm(buildEmptyAttemptForm(attemptPayload.corridorLength_m));
        return;
      }

      if (!attempt1Result) {
        setInlineError('A primeira tentativa do TC6 precisa ser concluída antes da segunda.');
        return;
      }

      const payload = buildTc6Payload(attempt1Result, {
        attempt2: attemptPayload,
        workflowStatus: 'completed',
        selectedReferenceKey,
        chartImages: {
          ...savedChartImages,
          ...currentImages,
        },
        patientContext: effectivePatientContext,
      });
      setShowMobileFinalModal(false);
      setFinalModalError(null);
      onNext(payload);
    } finally {
      setSavingAttempt(false);
    }
  };

  const handleCompleteWithOneAttempt = () => {
    if (!attempt1Result) {
      setInlineError('A primeira tentativa ainda não foi concluída.');
      return;
    }

    const payload = buildTc6Payload(attempt1Result, {
      workflowStatus: 'completed',
      selectedReferenceKey,
      chartImages: savedChartImages,
      patientContext: effectivePatientContext,
    });
    onNext(payload);
  };

  const handleStartSecondAttempt = () => {
    if (!attempt1Result) {
      setInlineError('A primeira tentativa ainda não foi concluída.');
      return;
    }
    setAttemptNumber(2);
    setAttemptStage('attempt2');
    setDesktopWorkflowStage('application');
    setInlineError(null);
    setBaselineModalError(null);
    setFinalModalError(null);
    setPerMinuteCompleted(Array(6).fill(false));
    resetMinutePrompt();
    setForm(buildEmptyAttemptForm(attempt1Result.corridorLength_m));
  };

  const confirmBaselineModal = () => {
    const validationMessage = getStartValidationMessage();
    if (validationMessage) {
      setBaselineModalError(validationMessage);
      return;
    }

    startCurrentAttempt();
  };

  const handleEnterDesktopClosingStage = () => {
    if (!form.startedAt && form.elapsed === 0) {
      setInlineError('Inicie o TC6 antes de seguir para o encerramento.');
      return;
    }

    setForm((current) => ({
      ...current,
      running: false,
      endedAt: current.elapsed >= 360 ? current.endedAt ?? toIsoNow() : current.endedAt,
    }));
    setDesktopWorkflowStage('closing');
    setInlineError(null);
  };

  const handleReturnToDesktopApplication = () => {
    setDesktopWorkflowStage('application');
    setInlineError(null);
  };

  const confirmFinalModal = async () => {
    if (!finalComplete) {
      setFinalModalError(`Preencha ${finalMissingFields.join(', ')} antes de concluir esta tentativa.`);
      return;
    }

    await handleSaveAttempt({ bypassMobileFinalGuard: true });
  };

  const currentAttemptCards =
    attemptStage === 'decision'
      ? buildAttemptSummaryCards(attempt1Result, selectedReferenceKey)
      : buildAttemptSummaryCards(currentAttemptPreview.attempt, selectedReferenceKey);

  const renderDistanceChartContent = () => {
    const speedSeries = currentAttemptPreview.attempt.per_minute_speed_kmh.map((speed, index) => ({
      minute: index + 1,
      speed,
    }));

    if (!speedSeries.length) {
      return (
        <div className="flex h-full flex-col justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center">
          <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Performance do teste</div>
          <div className="mt-3 text-3xl font-black text-slate-900">
            {currentAttemptPreview.attempt.derivedMetrics?.meanSpeed_kmh != null
              ? `${currentAttemptPreview.attempt.derivedMetrics.meanSpeed_kmh} km/h`
              : '—'}
          </div>
          <div className="mt-2 text-sm text-slate-500">
            A velocidade média é apenas um indicador complementar. O minuto a minuto pode ficar em branco sem bloquear o TC6.
          </div>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={speedSeries} margin={{ top: 12, right: 20, left: 4, bottom: 12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="minute" allowDecimals={false} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="speed" name="Velocidade por minuto (km/h)" stroke="#2563eb" strokeWidth={3} dot />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderVitalsChartContent = () => (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={currentAttemptPreview.vitalsSeries} margin={{ top: 12, right: 20, left: 4, bottom: 12 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="minute" allowDecimals={false} />
        <YAxis yAxisId="left" stroke="#2563eb" />
        <YAxis yAxisId="right" orientation="right" stroke="#0f766e" domain={[70, 100]} />
        <Tooltip />
        <Legend />
        <ReferenceLine yAxisId="right" y={88} stroke="#ef4444" strokeDasharray="4 4" />
        <Line yAxisId="right" type="monotone" dataKey="spo2" name="SpO2 (%)" stroke="#0f766e" strokeWidth={3} dot />
        <Line yAxisId="left" type="monotone" dataKey="hr" name="FC (bpm)" stroke="#2563eb" strokeWidth={3} dot />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderAttemptSummary = (attempt: Tc6AttemptPayload | null, attemptLabel: string, images?: { distance?: string | null; vitals?: string | null }) => {
    if (!attempt) return null;
    const vitalsSeriesData = getTc6VitalsSeries(attempt);
    const primaryReference = computeTC6PrimaryReference(attempt, selectedReferenceKey);
    const speedSeriesData = Array.isArray(attempt.per_minute_speed_kmh)
      ? attempt.per_minute_speed_kmh.map((speed, index) => ({ minute: index + 1, speed }))
      : [];
    return (
      <div className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <BorderTitle
            title={attemptLabel}
            subtitle={`${attempt.total_distance_m} m · ${primaryReference?.percentPredicted != null ? `${primaryReference.percentPredicted.toFixed(1)}% do previsto` : getTC6ReferenceLabel(selectedReferenceKey)}`}
          />
          <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            {attempt.stop_report?.intervals?.length ? `${attempt.stop_report.intervals.length} parada(s)` : 'Sem paradas'}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Resumo da tentativa</div>
            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-center justify-between">
                <span>Corredor</span>
                <span className="font-semibold text-slate-900">{attempt.corridorLength_m} m</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Voltas</span>
                <span className="font-semibold text-slate-900">{attempt.laps}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Ultima volta parcial</span>
                <span className="font-semibold text-slate-900">{attempt.last_lap_meters} m</span>
              </div>
                <div className="flex items-center justify-between">
                  <span>SpO2</span>
                  <span className="font-semibold text-slate-900">
                    {attempt.baseline.spo2 ?? '—'} {'->'} {attempt.final.spo2 ?? '—'}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>FC</span>
                  <span className="font-semibold text-slate-900">
                    {attempt.baseline.hr ?? '—'} {'->'} {attempt.final.hr ?? '—'} bpm
                  </span>
                </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Interpretação automática</div>
            <p className="text-sm leading-relaxed text-slate-700">
              {primaryReference?.predicted != null
                ? `Distância de ${attempt.total_distance_m} m com ${primaryReference.percentPredicted?.toFixed(1) ?? '—'}% do previsto pela equação ${primaryReference.label}.`
                : `Distância de ${attempt.total_distance_m} m, sem comparação prevista suficiente pelos dados antropométricos salvos.`}
            </p>
            {attempt.stop_report?.intervals?.length ? (
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                Tempo total interrompido: {attempt.stop_report.total_stopped_fmt}.
              </p>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Performance por minuto</div>
            {images?.distance ? (
              <img src={images.distance} alt={`${attemptLabel} performance por minuto`} className="w-full rounded-xl border border-slate-100" />
            ) : (
              <div className="h-64">
                {speedSeriesData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={speedSeriesData} margin={{ top: 12, right: 20, left: 4, bottom: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="minute" allowDecimals={false} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="speed" name="Velocidade por minuto (km/h)" stroke="#2563eb" strokeWidth={3} dot />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full flex-col justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-black text-slate-900">
                      {attempt.derivedMetrics?.meanSpeed_kmh != null ? `${attempt.derivedMetrics.meanSpeed_kmh} km/h` : '—'}
                    </div>
                    <div className="mt-2 text-sm text-slate-500">
                      Velocidade média disponível apenas como indicador complementar. O gráfico por minuto depende do registro opcional minuto a minuto.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">SpO2 e FC</div>
            {images?.vitals ? (
              <img src={images.vitals} alt={`${attemptLabel} sinais vitais`} className="w-full rounded-xl border border-slate-100" />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={vitalsSeriesData} margin={{ top: 12, right: 20, left: 4, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="minute" allowDecimals={false} />
                    <YAxis yAxisId="left" stroke="#2563eb" />
                    <YAxis yAxisId="right" orientation="right" stroke="#0f766e" domain={[70, 100]} />
                    <Tooltip />
                    <Legend />
                    <ReferenceLine yAxisId="right" y={88} stroke="#ef4444" strokeDasharray="4 4" />
                    <Line yAxisId="right" type="monotone" dataKey="spo2" name="SpO2 (%)" stroke="#0f766e" strokeWidth={3} dot />
                    <Line yAxisId="left" type="monotone" dataKey="hr" name="FC (bpm)" stroke="#2563eb" strokeWidth={3} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderMobileCaptureModeSelector = () => (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Aplicação móvel</div>
          <h4 className="mt-2 text-lg font-semibold text-slate-900">Escolha como registrar o teste</h4>
          <p className="mt-1 text-sm text-slate-600">
            O modo assistido abre os registros por minuto durante a caminhada. O simplificado deixa as pendências para o encerramento.
          </p>
        </div>
        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
          {mobileCaptureMode === 'assisted' ? 'Assistido' : 'Simplificado'}
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        <button
          type="button"
          onClick={() => setMobileCaptureMode('assisted')}
          className={`rounded-2xl border p-4 text-left ${
            mobileCaptureMode === 'assisted' ? 'border-primary-500 bg-primary-50' : 'border-slate-200 bg-slate-50 hover:bg-white'
          }`}
        >
          <div className="font-semibold text-slate-900">Assistido em tempo real</div>
          <div className="mt-1 text-sm text-slate-600">Mantém avisos por minuto, permite preencher na hora e continuar com a opção de completar depois.</div>
        </button>
        <button
          type="button"
          onClick={() => setMobileCaptureMode('simplified')}
          className={`rounded-2xl border p-4 text-left ${
            mobileCaptureMode === 'simplified' ? 'border-primary-500 bg-primary-50' : 'border-slate-200 bg-slate-50 hover:bg-white'
          }`}
        >
          <div className="font-semibold text-slate-900">Simplificado</div>
          <div className="mt-1 text-sm text-slate-600">Durante a caminhada mostra só tempo, voltas, parada/retomar e o minuto atual. O restante fica para fechar no fim.</div>
        </button>
      </div>
    </div>
  );

  const renderMobileMinuteRegisterSection = () => (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <BorderTitle
        title="Registro minuto a minuto"
        subtitle="Somente o essencial: SpO2, FC, voltas acumuladas e metros após a última volta."
      />
      <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        {minuteCompletedCount}/6 minutos preenchidos. Os minutos em branco não impedem concluir o TC6.
      </div>
      <div className="mt-4 space-y-3">
        {form.perMinute.map((entry, index) => {
          const minuteComplete =
            hasFilledMeasure(entry.hr) &&
            hasFilledMeasure(entry.spo2) &&
            hasFilledMeasure(entry.lapTotal) &&
            hasFilledMeasure(entry.extraMeters);
          return (
            <div key={entry.minute} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-900">{entry.minute}º minuto</div>
                <div
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                    minuteComplete ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {minuteComplete ? 'Completo' : 'Pendente'}
                </div>
              </div>

              <div className="mt-3 grid gap-3">
                <Field label="SpO2" type="number" suffix="%" value={entry.spo2} onChange={(value) => updatePerMinuteField(index, 'spo2', value)} />
                <Field label="FC" type="number" suffix="bpm" value={entry.hr} onChange={(value) => updatePerMinuteField(index, 'hr', value)} />
                <Field label="Voltas acumuladas" type="number" value={entry.lapTotal} onChange={(value) => updatePerMinuteField(index, 'lapTotal', value)} />
                <Field label="Metros após a última volta" type="number" suffix="m" value={entry.extraMeters} onChange={(value) => updatePerMinuteField(index, 'extraMeters', value)} />
              </div>

              <button
                type="button"
                onClick={() => syncCurrentCounterToMinute(index)}
                className="mt-3 inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Ruler className="mr-1 h-3.5 w-3.5" />
                Usar contador atual
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderMobileFinalizationSection = (showCancelButton = true) => (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <BorderTitle
        title="Finalização"
        subtitle="Ao concluir, o sistema abre o registro final de SpO2, FC, PA e Borg em modal."
      />
      {!finalComplete ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          Valores finais ainda pendentes: {finalMissingFields.join(', ')}.
        </div>
      ) : null}
      <div className="mt-4 grid gap-2">
        <button
          type="button"
          onClick={() => void handleSaveAttempt()}
          disabled={savingAttempt}
          className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {attemptNumber === 1 ? 'Salvar 1a tentativa' : 'Concluir TC6'}
          <Check className="ml-2 h-4 w-4" />
        </button>
        {showCancelButton ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </button>
        ) : null}
      </div>
    </div>
  );

  const renderDesktopPendingPanel = () => (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <BorderTitle title="Registros complementares" subtitle="Use esta coluna como checklist. O minuto a minuto é opcional e não bloqueia o salvamento." />
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div className="font-semibold text-slate-900">Basal</div>
            <div className="mt-1 text-slate-600">{baselineComplete ? 'Completo' : `Faltam: ${baselineMissingFields.join(', ')}`}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div className="font-semibold text-slate-900">Minuto a minuto</div>
            <div className="mt-1 text-slate-600">
              {minuteCompletedCount}/6 minutos completos{pendingMinutesCount > 0 ? ` · ${pendingMinutesCount} sem registro` : ''}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div className="font-semibold text-slate-900">Final</div>
            <div className="mt-1 text-slate-600">{finalComplete ? 'Completo' : `Faltam: ${finalMissingFields.join(', ')}`}</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-900 p-4 text-white shadow-sm">
        <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Aplicação atual</div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Tempo</div>
            <div className="mt-1 text-3xl font-black tabular-nums">{formatClock(form.elapsed)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Distância estimada</div>
            <div className="mt-1 text-3xl font-black">{currentAttemptPreview.attempt.total_distance_m} m</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Estado</div>
            <div className="mt-1 text-sm font-semibold text-white">
              {attemptFinished ? 'Pronto para encerrar' : form.running ? 'Cronômetro em execução' : form.elapsed > 0 ? 'Pausado para revisão' : 'Ainda não iniciado'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h3 className="text-xl font-black tracking-tight text-slate-900">Teste de Caminhada de 6 minutos (TC6)</h3>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
              Runner V2 baseado no fluxo do Revalidatie. O resultado continua salvo individualmente, com uma ou duas tentativas, histórico de paradas e gráficos híbridos para o laudo.
            </p>
          </div>
          <div className="min-w-[260px] rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Equação de referência</span>
              <select
                value={selectedReferenceKey}
                onChange={(event) => setSelectedReferenceKey(normalizeTC6ReferenceKey(event.target.value))}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {TC6_REFERENCE_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-3 text-xs text-slate-500">
              A referência selecionada alimenta a interpretação clínica e o PDF do TC6.
            </div>
            <button
              type="button"
              onClick={() => {
                setApplicationMode((current) => !current);
                setShowMobileBaselineModal(false);
                setShowMobileFinalModal(false);
                setBaselineModalError(null);
                setFinalModalError(null);
                resetMinutePrompt();
              }}
              className={`mt-4 inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-semibold ${
                applicationMode
                  ? 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Smartphone className="mr-2 h-4 w-4" />
              {applicationMode ? 'Sair da visualizacao em celular' : 'Visualizar em celular'}
            </button>
          </div>
        </div>
      </div>

      {applicationMode ? (
        <div className="rounded-3xl border border-blue-200 bg-blue-50/80 p-4 text-sm text-blue-950 shadow-sm">
          O modo de visualização em celular agora usa um fluxo próprio para aplicação em tempo real, com foco em cronômetro, voltas e registro clínico essencial.
        </div>
      ) : null}

      {inlineError ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <span>{inlineError}</span>
          </div>
        </div>
      ) : null}

      <AssessmentWeightAdjustmentField
        value={weightKg}
        baselineValue={initialWeightKg}
        onChange={setWeightKg}
        helperText="Use quando o peso aferido nesta avaliação estiver diferente do cadastro. O ajuste local entra nos cálculos do TC6 e é salvo apenas neste teste."
      />

      {applicationMode ? (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-slate-100">
          <div className="mx-auto min-h-full w-full max-w-screen-md px-4 py-4 sm:px-6">
            <div className="space-y-4">
              <div className="rounded-3xl bg-slate-900 p-4 text-white shadow-xl">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">TC6 Mobile</div>
                    <div className="mt-1 text-lg font-semibold">Tela de aplicação em viewport real</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setApplicationMode(false);
                      setShowMobileBaselineModal(false);
                      setShowMobileFinalModal(false);
                      setBaselineModalError(null);
                      setFinalModalError(null);
                      resetMinutePrompt();
                    }}
                    className="inline-flex items-center rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
                  >
                    <Smartphone className="mr-2 h-4 w-4" />
                    Sair
                  </button>
                </div>
              </div>

              {inlineError ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4" />
                    <span>{inlineError}</span>
                  </div>
                </div>
              ) : null}

              {attemptStage === 'decision' ? (
                <div className="space-y-4">
                  <div className="rounded-3xl border border-amber-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <BorderTitle
                        title="Primeira tentativa concluída"
                        subtitle="Escolha se deseja encerrar a avaliação ou iniciar a segunda tentativa."
                      />
                      <div className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                        1ª tentativa salva
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3">
                      {buildAttemptSummaryCards(attempt1Result, selectedReferenceKey).map((card) => (
                        <AttemptMetricCard key={`mobile-decision-${card.label}`} label={card.label} value={card.value} note={card.note} />
                      ))}
                    </div>

                    <div className="mt-4 grid gap-2">
                      <button
                        type="button"
                        onClick={handleCompleteWithOneAttempt}
                        className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700"
                      >
                        Finalizar com 1 tentativa
                        <Check className="ml-2 h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={handleStartSecondAttempt}
                        className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Iniciar 2a tentativa
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : applicationBaselineStage ? (
                <div className="space-y-4">
                  {attemptNumber === 2 && attempt1Result ? (
                    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">1a tentativa salva</div>
                      <div className="grid gap-3">
                        {buildAttemptSummaryCards(attempt1Result, selectedReferenceKey).map((card) => (
                          <AttemptMetricCard key={`mobile-attempt1-${card.label}`} label={card.label} value={card.value} note={card.note} />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {renderMobileCaptureModeSelector()}

                  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Modo Celular</div>
                        <h4 className="mt-2 text-xl font-semibold text-slate-900">Registro basal antes do cronômetro</h4>
                        <p className="mt-1 text-sm text-slate-600">
                          Preencha os dados iniciais e depois inicie o TC6 em tela limpa.
                        </p>
                      </div>
                      <div className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                        {attemptNumber === 1 ? 'Tentativa 1/2' : 'Tentativa 2/2'}
                      </div>
                    </div>

                    <div className="mt-4">
                      <Field
                        label="Corredor"
                        type="number"
                        min={1}
                        step={1}
                        suffix="m"
                        value={form.corridor}
                        disabled={corridorLocked}
                        onChange={(value) => setForm((current) => ({ ...current, corridor: Math.max(1, Number(value) || 0) }))}
                      />
                    </div>

                    {!baselineComplete ? (
                      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Preencha antes de iniciar: {baselineMissingFields.join(', ')}.
                      </div>
                    ) : null}

                    <div className="mt-4 grid gap-3">
                      <Field label="SpO2" type="number" suffix="%" value={form.baseline.spo2} onChange={(value) => updateBaselineField('spo2', value)} error={baselineMissingFields.includes('SpO2')} />
                      <Field label="FC" type="number" suffix="bpm" value={form.baseline.hr} onChange={(value) => updateBaselineField('hr', value)} error={baselineMissingFields.includes('FC')} />
                      <Field label="PA sistolica" type="number" suffix="mmHg" value={form.baseline.bpSys} onChange={(value) => updateBaselineField('bpSys', value)} error={baselineMissingFields.includes('PA sistolica')} />
                      <Field label="PA diastolica" type="number" suffix="mmHg" value={form.baseline.bpDia} onChange={(value) => updateBaselineField('bpDia', value)} error={baselineMissingFields.includes('PA diastolica')} />
                      <BorgField label="Borg dispneia" value={form.baseline.borgDyspnea} onChange={(value) => updateBaselineField('borgDyspnea', value)} error={baselineMissingFields.includes('Borg dispneia')} />
                      <BorgField label="Borg fadiga" value={form.baseline.borgFatigue} onChange={(value) => updateBaselineField('borgFatigue', value)} error={baselineMissingFields.includes('Borg fadiga')} />
                    </div>

                    <div className="mt-5 grid gap-2">
                      <button
                        type="button"
                        onClick={confirmBaselineModal}
                        className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
                      >
                        <Play className="mr-2 h-4 w-4" />
                        Iniciar TC6
                      </button>
                      <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              ) : applicationRunningStage ? (
                <div className="space-y-4">
                  {attemptNumber === 2 && attempt1Result ? (
                    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">1a tentativa salva</div>
                      <div className="grid gap-3">
                        {buildAttemptSummaryCards(attempt1Result, selectedReferenceKey).map((card) => (
                          <AttemptMetricCard key={`mobile-running-attempt1-${card.label}`} label={card.label} value={card.value} note={card.note} />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="rounded-3xl border border-emerald-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">TC6 em andamento</div>
                        <h4 className="mt-2 text-xl font-semibold text-slate-900">Aplicação em tempo real</h4>
                        <p className="mt-1 text-sm text-slate-600">
                          {mobileSimplifiedMode
                            ? 'Durante a caminhada, a tela fica reduzida ao essencial. As pendências serão fechadas no encerramento.'
                            : 'Tela focada no cronômetro, nas voltas e no registro clínico do momento.'}
                        </p>
                      </div>
                      <div className="space-y-2 text-right">
                        <div className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                          {attemptNumber === 1 ? 'Tentativa 1/2' : 'Tentativa 2/2'}
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {mobileCaptureMode === 'assisted' ? 'Fluxo assistido' : 'Fluxo simplificado'}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-center">
                      <div className="text-xs font-black uppercase tracking-[0.2em] text-emerald-700">Tempo restante</div>
                      <div className="mt-2 font-mono text-6xl font-black tabular-nums text-emerald-700">
                        {String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:{String(remainingSeconds % 60).padStart(2, '0')}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Distância total</div>
                        <div className="mt-2 text-3xl font-black text-slate-900">{currentAttemptPreview.attempt.total_distance_m} m</div>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Estado</div>
                        <div className="mt-2 text-base font-semibold text-slate-900">
                          {form.patientStopped ? 'Paciente parado' : form.running ? 'Em movimento' : attemptFinished ? 'Pronto para fechar' : 'Pausado'}
                        </div>
                        <div className="mt-1 text-sm text-slate-500">{form.stopIntervals.length} parada(s) registradas</div>
                      </div>
                    </div>

                    {mobileSimplifiedMode ? (
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                        <div className="font-semibold text-slate-900">{passiveMinute}º minuto em foco</div>
                        <div className="mt-1">
                          {attemptFinished
                            ? 'O cronômetro chegou a 6:00. Complete as pendências abaixo para concluir a tentativa.'
                            : `Pendências atuais: ${pendingMinutesCount} minuto(s) sem registro completo. Elas podem ser resolvidas no encerramento.`}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Voltas</div>
                    <div className="mt-4 flex items-center justify-center gap-4">
                      <button
                        type="button"
                        onClick={() => incrementLapCounter(-1)}
                        className="inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-white text-2xl text-slate-700 hover:bg-slate-50"
                      >
                        <Minus className="h-5 w-5" />
                      </button>
                      <div className="min-w-[4ch] text-center text-5xl font-black text-slate-900">{form.lapsCounter}</div>
                      <button
                        type="button"
                        onClick={() => incrementLapCounter(1)}
                        className="inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-white text-2xl text-slate-700 hover:bg-slate-50"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="mt-4">
                      <Field
                        label="Ultima volta parcial"
                        type="number"
                        min={0}
                        step={1}
                        suffix="m"
                        value={form.liveLastLapMeters}
                        onChange={(value) =>
                          setForm((current) => ({
                            ...current,
                            liveLastLapMeters: Math.max(0, Number(value) || 0),
                          }))
                        }
                      />
                    </div>

                    <div className="mt-4 grid gap-2">
                      {form.running ? (
                        <button
                          type="button"
                          onClick={handlePause}
                          className="inline-flex items-center justify-center rounded-xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white hover:bg-amber-600"
                        >
                          <Pause className="mr-2 h-4 w-4" />
                          Pausar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleStart}
                          className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
                        >
                          <Play className="mr-2 h-4 w-4" />
                          {form.elapsed > 0 ? 'Retomar caminhada' : 'Iniciar'}
                        </button>
                      )}

                      {!form.patientStopped ? (
                        <button
                          type="button"
                          onClick={() => setForm((current) => ({ ...current, pendingStopReason: true }))}
                          className="inline-flex items-center justify-center rounded-xl border border-red-200 bg-white px-4 py-3 text-sm font-semibold text-red-700 hover:bg-red-50"
                        >
                          <AlertTriangle className="mr-2 h-4 w-4" />
                          Registrar parada
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={resumeFromStop}
                          className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                        >
                          <Check className="mr-2 h-4 w-4" />
                          Retomar caminhada
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={resetCurrentAttempt}
                        className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Reiniciar tentativa
                      </button>
                    </div>
                  </div>

                  {form.pendingStopReason ? (
                    <div className="rounded-3xl border border-red-200 bg-red-50 p-5 shadow-sm">
                      <div className="text-sm font-semibold text-red-900">Selecione o motivo da parada</div>
                      <div className="mt-3 grid gap-3">
                        {STOP_REASON_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => beginStop(option.value)}
                            className="rounded-2xl border border-red-200 bg-white p-4 text-left hover:bg-red-50"
                          >
                            <div className="font-semibold text-slate-900">{option.label}</div>
                            <div className="mt-1 text-sm text-slate-600">{option.description}</div>
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm((current) => ({ ...current, pendingStopReason: false }))}
                        className="mt-3 text-sm font-medium text-red-700 hover:text-red-800"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : null}

                  {form.stopIntervals.length ? (
                    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Paradas registradas</div>
                      <div className="space-y-2">
                        {form.stopIntervals.map((interval, index) => {
                          const end = interval.end ?? form.elapsed;
                          const duration = Math.max(0, end - interval.start);
                          return (
                            <div key={`${interval.start}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                              <div className="font-semibold text-slate-900">{formatTc6StopReasonLabel(interval.reason)}</div>
                              <div className="mt-1">
                                {String(Math.floor(interval.start / 60)).padStart(2, '0')}:{String(interval.start % 60).padStart(2, '0')} {'->'}{' '}
                                {interval.end == null ? 'em andamento' : `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`}
                              </div>
                              <div className="mt-1 text-slate-500">
                                Duracao: {String(Math.floor(duration / 60)).padStart(2, '0')}:{String(duration % 60).padStart(2, '0')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {(mobileAssistedMode || attemptFinished) ? renderMobileMinuteRegisterSection() : null}

                  {mobileSimplifiedMode && !attemptFinished ? (
                    <div className="rounded-3xl border border-slate-200 bg-white p-5 text-sm text-slate-700 shadow-sm">
                      O fluxo simplificado mantém o foco no cronômetro e nas voltas. Quando o tempo terminar, esta tela passa a mostrar as pendências para fechamento da tentativa.
                    </div>
                  ) : null}

                  {(mobileAssistedMode || attemptFinished) ? renderMobileFinalizationSection() : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            {currentAttemptCards.map((card) => (
              <AttemptMetricCard key={card.label} label={card.label} value={card.value} note={card.note} />
            ))}
          </div>

          {attemptStage === 'decision' ? (
            <div className="space-y-5">
              <div className="rounded-3xl border border-amber-200 bg-amber-50/60 p-5 shadow-sm">
                <BorderTitle
                  title="Primeira tentativa concluída"
                  subtitle="O rascunho do TC6 já foi salvo. Agora você pode encerrar com 1 tentativa ou iniciar a 2ª tentativa usando o mesmo corredor."
                />
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleCompleteWithOneAttempt}
                    className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                  >
                    Finalizar com 1 tentativa
                    <Check className="ml-2 h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleStartSecondAttempt}
                    className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Iniciar 2a tentativa
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </button>
                </div>
              </div>

              {renderAttemptSummary(attempt1Result, 'TC6 - 1a tentativa', {
                distance: savedChartImages.attempt1_distance_payload ?? null,
                vitals: savedChartImages.attempt1_vitals_payload ?? null,
              })}
            </div>
          ) : desktopClosingStage ? (
            <div className="space-y-5">
              {attemptNumber === 2 && attempt1Result
                ? renderAttemptSummary(attempt1Result, 'TC6 - 1a tentativa salva', {
                    distance: savedChartImages.attempt1_distance_payload ?? null,
                    vitals: savedChartImages.attempt1_vitals_payload ?? null,
                  })
                : null}

              <div className="rounded-3xl border border-amber-200 bg-amber-50/70 p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <BorderTitle
                    title="Encerramento da tentativa"
                    subtitle="Revise as pendências abaixo e salve a tentativa sem voltar a procurar o botão de conclusão pela tela."
                  />
                  <div className="flex flex-wrap gap-3">
                    {!attemptFinished ? (
                      <button
                        type="button"
                        onClick={handleReturnToDesktopApplication}
                        className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Voltar ao cronômetro
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void handleSaveAttempt()}
                      disabled={savingAttempt}
                      className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {attemptNumber === 1 ? 'Salvar 1a tentativa' : 'Concluir TC6'}
                      <Check className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-5">
                  <div className="grid gap-5 xl:grid-cols-2">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="mb-4 flex items-center gap-2">
                        <HeartPulse className="h-4 w-4 text-rose-500" />
                        <BorderTitle title="Medidas basais" subtitle="Revise apenas se algo ficou pendente no início." />
                      </div>
                      {!baselineComplete ? (
                        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          Pendentes: {baselineMissingFields.join(', ')}.
                        </div>
                      ) : null}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="SpO2" type="number" suffix="%" value={form.baseline.spo2} onChange={(value) => updateBaselineField('spo2', value)} error={baselineMissingFields.includes('SpO2')} />
                        <Field label="FC" type="number" suffix="bpm" value={form.baseline.hr} onChange={(value) => updateBaselineField('hr', value)} error={baselineMissingFields.includes('FC')} />
                        <Field label="PA sistolica" type="number" suffix="mmHg" value={form.baseline.bpSys} onChange={(value) => updateBaselineField('bpSys', value)} error={baselineMissingFields.includes('PA sistolica')} />
                        <Field label="PA diastolica" type="number" suffix="mmHg" value={form.baseline.bpDia} onChange={(value) => updateBaselineField('bpDia', value)} error={baselineMissingFields.includes('PA diastolica')} />
                        <BorgField label="Borg dispneia" value={form.baseline.borgDyspnea} onChange={(value) => updateBaselineField('borgDyspnea', value)} error={baselineMissingFields.includes('Borg dispneia')} />
                        <BorgField label="Borg fadiga" value={form.baseline.borgFatigue} onChange={(value) => updateBaselineField('borgFatigue', value)} error={baselineMissingFields.includes('Borg fadiga')} />
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="mb-4 flex items-center gap-2">
                        <Droplet className="h-4 w-4 text-blue-500" />
                        <BorderTitle title="Medidas finais" subtitle="Necessárias para liberar o salvamento da tentativa." />
                      </div>
                      {!finalComplete ? (
                        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
                          Antes de salvar, complete: {finalMissingFields.join(', ')}.
                        </div>
                      ) : null}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="SpO2" type="number" suffix="%" value={form.final.spo2} onChange={(value) => updateFinalField('spo2', value)} error={finalMissingFields.includes('SpO2')} />
                        <Field label="FC" type="number" suffix="bpm" value={form.final.hr} onChange={(value) => updateFinalField('hr', value)} error={finalMissingFields.includes('FC')} />
                        <Field label="PA sistolica" type="number" suffix="mmHg" value={form.final.bpSys} onChange={(value) => updateFinalField('bpSys', value)} error={finalMissingFields.includes('PA sistolica')} />
                        <Field label="PA diastolica" type="number" suffix="mmHg" value={form.final.bpDia} onChange={(value) => updateFinalField('bpDia', value)} error={finalMissingFields.includes('PA diastolica')} />
                        <BorgField label="Borg dispneia" value={form.final.borgDyspnea} onChange={(value) => updateFinalField('borgDyspnea', value)} error={finalMissingFields.includes('Borg dispneia')} />
                        <BorgField label="Borg fadiga" value={form.final.borgFatigue} onChange={(value) => updateFinalField('borgFatigue', value)} error={finalMissingFields.includes('Borg fadiga')} />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="mb-4 flex items-center gap-2">
                      <Clock3 className="h-4 w-4 text-slate-600" />
                      <BorderTitle title="Registro minuto a minuto" subtitle="Concentre o fechamento da tentativa nesta tabela." />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-collapse text-left text-sm">
                        <thead>
                          <tr className="bg-white text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                            <th className="px-3 py-3">Min</th>
                            <th className="px-3 py-3">SpO2</th>
                            <th className="px-3 py-3">FC</th>
                            <th className="px-3 py-3">Voltas acumuladas</th>
                            <th className="px-3 py-3">Metros após a última volta</th>
                            <th className="px-3 py-3">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {form.perMinute.map((entry, index) => (
                            <tr key={entry.minute}>
                              <td className="px-3 py-3 font-black text-slate-900">{entry.minute}</td>
                              <td className="px-3 py-3">
                                <input
                                  type="number"
                                  value={entry.spo2}
                                  onChange={(event) => updatePerMinuteField(index, 'spo2', event.target.value)}
                                  className="w-24 rounded-lg border border-slate-200 px-2 py-1.5"
                                />
                              </td>
                              <td className="px-3 py-3">
                                <input
                                  type="number"
                                  value={entry.hr}
                                  onChange={(event) => updatePerMinuteField(index, 'hr', event.target.value)}
                                  className="w-24 rounded-lg border border-slate-200 px-2 py-1.5"
                                />
                              </td>
                              <td className="px-3 py-3">
                                <input
                                  type="number"
                                  value={entry.lapTotal}
                                  onChange={(event) => updatePerMinuteField(index, 'lapTotal', event.target.value)}
                                  className="w-28 rounded-lg border border-slate-200 px-2 py-1.5"
                                />
                              </td>
                              <td className="px-3 py-3">
                                <input
                                  type="number"
                                  value={entry.extraMeters}
                                  onChange={(event) => updatePerMinuteField(index, 'extraMeters', event.target.value)}
                                  className="w-32 rounded-lg border border-slate-200 px-2 py-1.5"
                                />
                              </td>
                              <td className="px-3 py-3">
                                <button
                                  type="button"
                                  onClick={() => syncCurrentCounterToMinute(index)}
                                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                >
                                  <Ruler className="mr-1 h-3.5 w-3.5" />
                                  Usar contador
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="grid gap-5 xl:grid-cols-2">
                    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                      <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Gráfico de distância</div>
                      <div className="h-64 rounded-xl border border-slate-100 bg-white p-2">{renderDistanceChartContent()}</div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                      <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Gráfico de SpO2 e FC</div>
                      <div className="h-64 rounded-xl border border-slate-100 bg-white p-2">{renderVitalsChartContent()}</div>
                    </div>
                  </div>
                </div>

                {renderDesktopPendingPanel()}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {attemptNumber === 2 && attempt1Result
                ? renderAttemptSummary(attempt1Result, 'TC6 - 1a tentativa salva', {
                    distance: savedChartImages.attempt1_distance_payload ?? null,
                    vitals: savedChartImages.attempt1_vitals_payload ?? null,
                  })
                : null}

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <BorderTitle
                    title={attemptNumber === 1 ? 'Aplicação da 1a tentativa' : 'Aplicação da 2a tentativa'}
                    subtitle="Durante a aplicação, a tela fica focada no cronômetro, nas voltas, nas paradas e no checklist de pendências."
                  />
                  <div className="rounded-full bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700">
                    {attemptNumber === 1 ? 'Tentativa 1/2' : 'Tentativa 2/2'}
                  </div>
                </div>

                <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-5">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <BorderTitle title="Controles do teste" subtitle="Cronômetro, contador de voltas e eventos de parada." />
                        <div className="rounded-2xl bg-slate-900 px-4 py-3 text-center text-white">
                          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Tempo</div>
                          <div className="mt-1 text-3xl font-black tabular-nums">
                            {String(Math.floor(form.elapsed / 60)).padStart(2, '0')}:{String(form.elapsed % 60).padStart(2, '0')}
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <Field
                          label="Corredor"
                          type="number"
                          min={1}
                          step={1}
                          suffix="m"
                          value={form.corridor}
                          disabled={corridorLocked}
                          onChange={(value) => setForm((current) => ({ ...current, corridor: Math.max(1, Number(value) || 0) }))}
                        />

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Voltas correntes</div>
                          <div className="mt-2 flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => incrementLapCounter(-1)}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <div className="text-3xl font-black text-slate-900">{form.lapsCounter}</div>
                            <button
                              type="button"
                              onClick={() => incrementLapCounter(1)}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <Field
                          label="Ultima volta parcial"
                          type="number"
                          min={0}
                          step={1}
                          suffix="m"
                          value={form.liveLastLapMeters}
                          onChange={(value) =>
                            setForm((current) => ({
                              ...current,
                              liveLastLapMeters: Math.max(0, Number(value) || 0),
                            }))
                          }
                        />

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Paradas</div>
                          <div className="mt-2 text-2xl font-black text-slate-900">{form.stopIntervals.length}</div>
                          <div className="mt-1 text-xs text-slate-500">{form.patientStopped ? 'Paciente em parada' : 'Sem parada ativa'}</div>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-3">
                        {form.running ? (
                          <button
                            type="button"
                            onClick={handlePause}
                            className="inline-flex items-center rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                          >
                            <Pause className="mr-2 h-4 w-4" />
                            Pausar
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleStart}
                            className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                          >
                            <Play className="mr-2 h-4 w-4" />
                            {form.elapsed > 0 ? 'Retomar caminhada' : 'Iniciar'}
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={resetCurrentAttempt}
                          className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          <RotateCcw className="mr-2 h-4 w-4" />
                          Reiniciar tentativa
                        </button>

                        {!form.patientStopped ? (
                          <button
                            type="button"
                            onClick={() => setForm((current) => ({ ...current, pendingStopReason: true }))}
                            className="inline-flex items-center rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                          >
                            <AlertTriangle className="mr-2 h-4 w-4" />
                            Registrar parada
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={resumeFromStop}
                            className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                          >
                            <Check className="mr-2 h-4 w-4" />
                            Retomar caminhada
                          </button>
                        )}

                        {form.elapsed > 0 ? (
                          <button
                            type="button"
                            onClick={handleEnterDesktopClosingStage}
                            className="inline-flex items-center rounded-xl border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-100"
                          >
                            <ChevronRight className="mr-2 h-4 w-4" />
                            {attemptFinished ? 'Encerrar e revisar pendências' : 'Revisar pendências'}
                          </button>
                        ) : null}
                      </div>

                      {!baselineComplete ? (
                        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                          Preencha os dados basais antes de iniciar: {baselineMissingFields.join(', ')}.
                        </div>
                      ) : null}

                      {form.pendingStopReason ? (
                        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4">
                          <div className="text-sm font-semibold text-red-900">Selecione o motivo da parada</div>
                          <div className="mt-3 grid gap-3 md:grid-cols-2">
                            {STOP_REASON_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => beginStop(option.value)}
                                className="rounded-2xl border border-red-200 bg-white p-4 text-left hover:bg-red-50"
                              >
                                <div className="font-semibold text-slate-900">{option.label}</div>
                                <div className="mt-1 text-sm text-slate-600">{option.description}</div>
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => setForm((current) => ({ ...current, pendingStopReason: false }))}
                            className="mt-3 text-sm font-medium text-red-700 hover:text-red-800"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : null}

                      {form.stopIntervals.length ? (
                        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                          <table className="w-full border-collapse text-left text-sm">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                              <tr>
                                <th className="px-4 py-3">Inicio</th>
                                <th className="px-4 py-3">Fim</th>
                                <th className="px-4 py-3">Duracao</th>
                                <th className="px-4 py-3">Motivo</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                              {form.stopIntervals.map((interval, index) => {
                                const end = interval.end ?? form.elapsed;
                                const duration = Math.max(0, end - interval.start);
                                return (
                                  <tr key={`${interval.start}-${index}`}>
                                    <td className="px-4 py-3 font-medium text-slate-900">{String(Math.floor(interval.start / 60)).padStart(2, '0')}:{String(interval.start % 60).padStart(2, '0')}</td>
                                    <td className="px-4 py-3 text-slate-700">
                                      {interval.end == null ? 'Em andamento' : `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">{String(Math.floor(duration / 60)).padStart(2, '0')}:{String(duration % 60).padStart(2, '0')}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatTc6StopReasonLabel(interval.reason)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="mb-4 flex items-center gap-2">
                        <HeartPulse className="h-4 w-4 text-rose-500" />
                        <BorderTitle title="Medidas basais" subtitle="Obrigatórias antes de iniciar o cronômetro." />
                      </div>
                      {!baselineComplete ? (
                        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          Pendentes: {baselineMissingFields.join(', ')}.
                        </div>
                      ) : null}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="SpO2" type="number" suffix="%" value={form.baseline.spo2} onChange={(value) => updateBaselineField('spo2', value)} error={baselineMissingFields.includes('SpO2')} />
                        <Field label="FC" type="number" suffix="bpm" value={form.baseline.hr} onChange={(value) => updateBaselineField('hr', value)} error={baselineMissingFields.includes('FC')} />
                        <Field label="PA sistolica" type="number" suffix="mmHg" value={form.baseline.bpSys} onChange={(value) => updateBaselineField('bpSys', value)} error={baselineMissingFields.includes('PA sistolica')} />
                        <Field label="PA diastolica" type="number" suffix="mmHg" value={form.baseline.bpDia} onChange={(value) => updateBaselineField('bpDia', value)} error={baselineMissingFields.includes('PA diastolica')} />
                        <BorgField label="Borg dispneia" value={form.baseline.borgDyspnea} onChange={(value) => updateBaselineField('borgDyspnea', value)} error={baselineMissingFields.includes('Borg dispneia')} />
                        <BorgField label="Borg fadiga" value={form.baseline.borgFatigue} onChange={(value) => updateBaselineField('borgFatigue', value)} error={baselineMissingFields.includes('Borg fadiga')} />
                      </div>
                    </div>
                  </div>

                  {renderDesktopPendingPanel()}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div
        aria-hidden="true"
        className="pointer-events-none fixed left-[-10000px] top-0 z-[-1] w-[980px] overflow-hidden bg-white p-4"
      >
        <div className="grid grid-cols-2 gap-6">
          <div ref={captureDistanceChartRef} className="h-[360px] rounded-xl border border-slate-100 bg-white p-3">
            {renderDistanceChartContent()}
          </div>
          <div ref={captureVitalsChartRef} className="h-[360px] rounded-xl border border-slate-100 bg-white p-3">
            {renderVitalsChartContent()}
          </div>
        </div>
      </div>

      {showEnc ? (
        <div className="fixed left-1/2 top-20 z-50 -translate-x-1/2">
          <div className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg">
            {encText}
          </div>
        </div>
      ) : null}

      {minutePrompt.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className={`w-full rounded-2xl border bg-white p-5 shadow-2xl ${applicationMode ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}
          >
            {applicationMode ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold uppercase text-blue-700">Registro automático</div>
                    <div className="text-2xl font-semibold text-slate-900">{minutePrompt.minute}º minuto</div>
                    <p className="text-sm text-slate-600">
                      Informe SpO2, FC e a distância percorrida após a última volta deste minuto.
                    </p>
                  </div>
                  <div className="text-4xl font-mono font-bold text-blue-600">{minutePromptCountdown}s</div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {minuteFieldConfigs.map((config) => (
                    <button
                      key={config.field}
                      type="button"
                      onClick={() => setActiveMinuteField(config.field)}
                      className={`flex flex-col gap-2 rounded-2xl border p-4 text-left ${
                        activeMinuteField === config.field ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase text-slate-500">{config.label}</span>
                        {config.icon}
                      </div>
                      <div className="text-3xl font-semibold text-slate-900">
                        {minutePrompt[config.field] || '--'}
                        <span className="ml-1 text-base text-slate-500">{minutePrompt[config.field] ? config.unit : ''}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  {keypadRows.map((row, rowIndex) => (
                    <Fragment key={`row-${rowIndex}`}>
                      {row.map((token) => (
                        <button
                          key={token}
                          type="button"
                          onClick={() => handleKeypadInput(token)}
                          className="h-14 rounded-xl bg-slate-100 text-lg font-semibold text-slate-800 hover:bg-slate-200"
                        >
                          {token}
                        </button>
                      ))}
                    </Fragment>
                  ))}
                  <button
                    type="button"
                    onClick={() => handleKeypadInput('back')}
                    className="col-span-2 h-14 rounded-xl bg-amber-100 font-semibold text-amber-800"
                  >
                    Apagar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleKeypadInput('clear')}
                    className="h-14 rounded-xl bg-rose-100 font-semibold text-rose-700"
                  >
                    Limpar
                  </button>
                </div>

                {minutePromptError ? <div className="mt-3 text-sm text-red-600">{minutePromptError}</div> : null}

                <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-slate-700">
                  Distância após a última volta: {minutePrompt.postTurn || '0'} m
                </div>
                <div className="mt-2 rounded-md border bg-slate-50 p-2 text-xs text-slate-500">
                  Distância acumulada até o minuto anterior: {minutePrompt.baseDistance} m
                </div>

                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    className="flex-1 rounded-md border px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    onClick={handleMinutePromptSkip}
                  >
                    Preencher depois
                  </button>
                  <button
                    type="button"
                    className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                    onClick={handleMinutePromptSave}
                  >
                    Salvar e continuar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="text-sm font-semibold text-blue-600">Registro automático</div>
                  <div className="text-lg font-semibold text-slate-900">{minutePrompt.minute}º minuto concluído</div>
                  <p className="text-sm text-slate-600">
                    Informe os sinais vitais e a distância percorrida neste minuto.
                  </p>
                  <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                    <span>O cronômetro permanece em execução.</span>
                    <span className="font-semibold text-blue-600">{minutePromptCountdown}s</span>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="text-sm">
                    SpO2 (%)
                    <input
                      inputMode="numeric"
                      className="mt-1 w-full rounded border px-2 py-1"
                      value={minutePrompt.spo2}
                      onChange={(event) => {
                        setMinutePrompt((current) => ({ ...current, spo2: event.target.value }));
                        setMinutePromptError(null);
                      }}
                      autoFocus
                    />
                  </label>
                  <label className="text-sm">
                    FC (bpm)
                    <input
                      inputMode="numeric"
                      className="mt-1 w-full rounded border px-2 py-1"
                      value={minutePrompt.hr}
                      onChange={(event) => {
                        setMinutePrompt((current) => ({ ...current, hr: event.target.value }));
                        setMinutePromptError(null);
                      }}
                    />
                  </label>
                </div>

                <label className="mt-3 block text-sm">
                  Metros após a última volta (m)
                  <input
                    inputMode="numeric"
                    className="mt-1 w-full rounded border px-2 py-1"
                    value={minutePrompt.postTurn}
                    onChange={(event) => {
                      setMinutePrompt((current) => ({ ...current, postTurn: event.target.value }));
                      setMinutePromptError(null);
                    }}
                    placeholder="ex: 12"
                  />
                </label>

                {minutePromptError ? <div className="mt-3 text-sm text-red-600">{minutePromptError}</div> : null}

                <div className="mt-3 rounded-md border bg-slate-50 p-2 text-xs text-slate-500">
                  Distância acumulada até o minuto anterior: {minutePrompt.baseDistance} m
                </div>

                <div className="mt-4 flex justify-between gap-2">
                  <button
                    type="button"
                    className="rounded-md border px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    onClick={handleMinutePromptSkip}
                  >
                    Preencher depois
                  </button>
                  <button
                    type="button"
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                    onClick={handleMinutePromptSave}
                  >
                    Salvar e continuar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {applicationMode && showMobileBaselineModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-4">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Preparacao</div>
              <h4 className="mt-2 text-lg font-semibold text-slate-900">Registre os valores basais</h4>
              <p className="mt-1 text-sm text-slate-600">
                O cronômetro do TC6 só pode iniciar com SpO2, FC, PA e Borg preenchidos.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="SpO2" type="number" suffix="%" value={form.baseline.spo2} onChange={(value) => updateBaselineField('spo2', value)} error={baselineMissingFields.includes('SpO2')} />
              <Field label="FC" type="number" suffix="bpm" value={form.baseline.hr} onChange={(value) => updateBaselineField('hr', value)} error={baselineMissingFields.includes('FC')} />
              <Field label="PA sistolica" type="number" suffix="mmHg" value={form.baseline.bpSys} onChange={(value) => updateBaselineField('bpSys', value)} error={baselineMissingFields.includes('PA sistolica')} />
              <Field label="PA diastolica" type="number" suffix="mmHg" value={form.baseline.bpDia} onChange={(value) => updateBaselineField('bpDia', value)} error={baselineMissingFields.includes('PA diastolica')} />
              <BorgField label="Borg dispneia" value={form.baseline.borgDyspnea} onChange={(value) => updateBaselineField('borgDyspnea', value)} error={baselineMissingFields.includes('Borg dispneia')} />
              <BorgField label="Borg fadiga" value={form.baseline.borgFatigue} onChange={(value) => updateBaselineField('borgFatigue', value)} error={baselineMissingFields.includes('Borg fadiga')} />
            </div>
            {baselineModalError ? (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {baselineModalError}
              </div>
            ) : null}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowMobileBaselineModal(false);
                  setBaselineModalError(null);
                }}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={confirmBaselineModal}
                className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Iniciar cronômetro
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {applicationMode && showMobileFinalModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-4">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-rose-600">Encerramento</div>
              <h4 className="mt-2 text-lg font-semibold text-slate-900">Registre os valores finais</h4>
              <p className="mt-1 text-sm text-slate-600">
                Antes de concluir a tentativa, complete os dados pos-teste obrigatorios.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="SpO2" type="number" suffix="%" value={form.final.spo2} onChange={(value) => updateFinalField('spo2', value)} error={finalMissingFields.includes('SpO2')} />
              <Field label="FC" type="number" suffix="bpm" value={form.final.hr} onChange={(value) => updateFinalField('hr', value)} error={finalMissingFields.includes('FC')} />
              <Field label="PA sistolica" type="number" suffix="mmHg" value={form.final.bpSys} onChange={(value) => updateFinalField('bpSys', value)} error={finalMissingFields.includes('PA sistolica')} />
              <Field label="PA diastolica" type="number" suffix="mmHg" value={form.final.bpDia} onChange={(value) => updateFinalField('bpDia', value)} error={finalMissingFields.includes('PA diastolica')} />
              <BorgField label="Borg dispneia" value={form.final.borgDyspnea} onChange={(value) => updateFinalField('borgDyspnea', value)} error={finalMissingFields.includes('Borg dispneia')} />
              <BorgField label="Borg fadiga" value={form.final.borgFatigue} onChange={(value) => updateFinalField('borgFatigue', value)} error={finalMissingFields.includes('Borg fadiga')} />
            </div>
            {finalModalError ? (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {finalModalError}
              </div>
            ) : null}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowMobileFinalModal(false);
                  setFinalModalError(null);
                }}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => void confirmFinalModal()}
                className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Concluir tentativa
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
